import {
  generateCurlSnippet,
  generateJavascriptSnippet,
  generatePythonSnippet,
  generateRubySnippet,
  generateCSnippet,
generateCppSnippet,
  generateJavaSnippet,
  generateShellSnippet,
  generateNodeSnippet,
  generatePHPSnippet,
  generateCSharpSnippet,
  generateSwiftSnippet,
  generateHTTPSnippet,
} from './snippet-generators';

export {
  generateCurlSnippet,
  generateJavascriptSnippet,
  generatePythonSnippet,
  generateRubySnippet,
  generateCSnippet,
generateCppSnippet,
  generateJavaSnippet,
  generateShellSnippet,
  generateNodeSnippet,
  generatePHPSnippet,
  generateCSharpSnippet,
  generateSwiftSnippet,
  generateHTTPSnippet,
};

// MIME types that indicate a file download response, mapped to file extensions
const DOWNLOAD_MIME_TO_EXT = {
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/octet-stream': 'bin',
};

// ─── Configurable endpoint ordering per tag ───────────────────────────────────
// Keys are tag names exactly as they appear in the OpenAPI spec.
// Values are arrays of endpoint summaries (api.name) in the desired display order.
// Endpoints whose summaries are NOT listed here are appended at the end in their
// original backend-provided order.
// Tags NOT listed here keep the backend order unchanged.
// If a tag's apis array is empty the sort is a no-op.
const TAG_ENDPOINT_ORDER = {
  'Billing - Azure': [
    'Get Summaries',
    'Get Summary Details',   // actual summary in the Azure spec (lowercase 's')
    'Download Summary',
    'Download Summary Recon File', // actual summary — no space between Summary and Recon
    'Download Summary Slip PDF',
    'Get Invoices',
    'Get Invoice Details',
    'Download Invoice',
    'Download Invoice Recon File',
    'Get Credit Notes',
    'Get Credit Note Details',
    'Download Credit Note',
  ],
  'Billing - CSP': [
 'Get Summaries',
    'Get Summary Details',       // actual summary in the CSP spec (uppercase 'S')
    'Download Summary',
    'Download Summary Recon File', // actual summary — no space between Summary and Recon
 'Download Summary Slip PDF',
    'Get Invoices',
    'Get Invoice Details',
 'Download Invoice',
    'Download Invoice Recon File',
    'Get Credit Notes',
    'Get Credit Note Details',
    'Download Credit Note',
  ],
};

/**
 * Sorts an array of api objects according to TAG_ENDPOINT_ORDER for the given tagName.
 *
 * - Apis whose name appears in the order array are placed first, in that exact order.
 * - Apis whose name is NOT in the order array are appended at the end, preserving
 *   their original relative order (stable sort behaviour).
 * - If no order is configured for the tag, the original array is returned as-is.
 */
function sortApisByTagOrder(apis, tagName) {
  const order = TAG_ENDPOINT_ORDER[tagName];
  if (!order || order.length === 0 || apis.length === 0) return apis;

  const orderIndex = (name) => {
    const idx = order.indexOf(name);
    return idx === -1 ? Infinity : idx;
  };

  return [...apis].sort((a, b) => {
    const ia = orderIndex(a.name);
    const ib = orderIndex(b.name);
if (ia !== ib) return ia - ib;
    // Both unlisted — preserve original relative order
    return apis.indexOf(a) - apis.indexOf(b);
  });
}

/**
 * Determines if an endpoint is a download API and what file type it produces.
 *
 * Detection strategy (in order of priority):
 * 1. Explicit download MIME types in response content (application/pdf, text/csv, etc.)
 * 2. Wildcard "*\/*" content type on a 2xx response combined with "download" in the
 *    endpoint summary or description — the default format is inferred from the
 *    `format` query parameter's enum values (csv/pdf/xlsx) or defaults to 'bin'.
 *
 * Returns { isDownload, downloadContentType, downloadFileExtension }
 */
function detectDownloadInfo(endpoint) {
  const responses = endpoint.responses || {};
  const summary = (endpoint.summary || '').toLowerCase();
  const description = (endpoint.description || '').toLowerCase();
  const isDownloadKeyword = summary.includes('download') || description.includes('download');

  for (const statusCode of Object.keys(responses)) {
    if (!String(statusCode).startsWith('2')) continue;
  const responseDef = responses[statusCode];
    if (!responseDef.content) continue;

    // Strategy 1: Explicit download MIME type
    for (const mimeType of Object.keys(DOWNLOAD_MIME_TO_EXT)) {
      if (responseDef.content[mimeType]) {
        return {
isDownload: true,
   downloadContentType: mimeType,
          downloadFileExtension: DOWNLOAD_MIME_TO_EXT[mimeType],
      };
      }
    }

    // Strategy 2: Wildcard "*/*" + download keyword in summary/description
    if (responseDef.content['*/*'] !== undefined && isDownloadKeyword) {
      // Try to infer file extension from the `format` query param's enum
      let ext = 'bin';
      let contentType = 'application/octet-stream';
      const formatParam = (endpoint.parameters || []).find(
     p => p.name === 'format' && p.in === 'query'
  );
      if (formatParam && formatParam.schema && formatParam.schema.enum) {
        const enums = formatParam.schema.enum.map(e => String(e).toLowerCase());
 const defaultVal = formatParam.schema.default
          ? String(formatParam.schema.default).toLowerCase()
          : null;
 // Use the default format value, or the first enum value
        const preferred = defaultVal || enums[0];
        if (preferred === 'csv' || enums.includes('csv')) {
          ext = defaultVal === 'pdf' ? 'pdf' : 'csv';
  contentType = defaultVal === 'pdf' ? 'application/pdf' : 'text/csv';
  } else if (preferred === 'pdf') {
          ext = 'pdf';
        contentType = 'application/pdf';
    } else if (preferred === 'xlsx') {
 ext = 'xlsx';
  contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
   }
      } else if (isDownloadKeyword) {
        // No format param — check description for hints
   if (description.includes('csv')) { ext = 'csv'; contentType = 'text/csv'; }
    else if (description.includes('pdf')) { ext = 'pdf'; contentType = 'application/pdf'; }
else if (description.includes('xlsx') || description.includes('excel')) {
  ext = 'xlsx';
    contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}
      }
      return {
      isDownload: true,
      downloadContentType: contentType,
        downloadFileExtension: ext,
      };
    }
  }

  return { isDownload: false, downloadContentType: null, downloadFileExtension: null };
}

const resolveRef = (spec, ref) => {
  const parts = ref.replace('#/', '').split('/');
  return parts.reduce((obj, part) => obj && obj[part], spec);
};

export const slugify = (text) => {
  if (!text) return '';
  return text
  .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
 .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export const parseOpenApiSpec = (openApiSpec) => {
  const baseUrl = openApiSpec.servers && openApiSpec.servers[0] ? openApiSpec.servers[0].url : '';
  const securitySchemes = openApiSpec.components && openApiSpec.components.securitySchemes;
  const groupedApis = {};

  for (const path in openApiSpec.paths) {
  for (const method in openApiSpec.paths[path]) {
const endpoint = openApiSpec.paths[path][method];

      let allParameters = [];
      if (endpoint.parameters) {
     allParameters = allParameters.concat(endpoint.parameters.map(p => {
      if (p.$ref) {
    return resolveRef(openApiSpec, p.$ref);
          }
      return p;
}));
      }

      // Add security scheme parameters
      if (securitySchemes && endpoint.security) {
    endpoint.security.forEach(securityObj => {
      const schemeName = Object.keys(securityObj)[0];
  if (securitySchemes[schemeName] && securitySchemes[schemeName].type === 'apiKey') {
          allParameters.push({
    name: securitySchemes[schemeName].name,
        in: securitySchemes[schemeName].in,
         description: securitySchemes[schemeName].description || 'Authentication token',
     required: true,
      schema: { type: 'string' }
   });
  }
        });
      }

   let requestBody = null;
      if (endpoint.requestBody) {
    const rb = endpoint.requestBody.content && endpoint.requestBody.content['application/json'];
     if (rb && rb.schema) {
          const schema = rb.schema.$ref ? resolveRef(openApiSpec, rb.schema.$ref) : rb.schema;
   requestBody = {
description: endpoint.requestBody.description || '',
         example: rb.example || schema.example || {}
       };

        // Add body parameters to the list for UI rendering
  if (schema.properties) {
   for (const propName in schema.properties) {
   const prop = schema.properties[propName];
        if (prop.type === 'array' && prop.items) {
  const itemsSchema = prop.items.$ref ? resolveRef(openApiSpec, prop.items.$ref) : prop.items;
   allParameters.push({
        name: propName,
  in: 'body',
        description: prop.description || '',
required: (schema.required && schema.required.includes(propName)) || false,
      schema: {
   type: 'array',
         items: itemsSchema
       }
          });
 } else {
        allParameters.push({
          name: propName,
           in: 'body',
  description: prop.description || '',
    required: (schema.required && schema.required.includes(propName)) || false,
     schema: { type: prop.type || 'object' }
    });
          }
     }
          }
        }
      }

    const responses = {};
      for (const statusCode in endpoint.responses) {
        const responseDef = endpoint.responses[statusCode];
  const content = responseDef.content && responseDef.content['application/json'];
        let body = {};
        if (content) {
 if (content.example) {
     body = content.example;
        } else if (content.schema) {
      const schema = content.schema.$ref ? resolveRef(openApiSpec, content.schema.$ref) : content.schema;
      body = schema.example || { message: responseDef.description };
  }
        }
        responses[statusCode] = {
          statusCode: parseInt(statusCode),
       description: responseDef.description,
    body: body,
      };
   }

   // Detect if this is a download API and what file type it produces
      const { isDownload, downloadContentType, downloadFileExtension } = detectDownloadInfo(endpoint);

      let description = endpoint.description || '';
   let requestSample = null;
      let responseSample = null;

      const sampleRegex = /<pre><code.*?>([\s\S]*?)<\/code><\/pre>/g;
      let cleanedDescription = description;
      const embeddedSamples = [];

    let match;
      while ((match = sampleRegex.exec(description)) !== null) {
        const codeContent = match[1].replace(/"/g, '"').trim();
  try {
 const parsedJson = JSON.parse(codeContent);
       embeddedSamples.push(parsedJson);
    cleanedDescription = cleanedDescription.replace(match[0], '');
   } catch (e) {
          // Not JSON, leave in description
        }
      }

  const api = {
 id: endpoint.operationId || `${method}-${path.replace(/\//g, '-')}`,
      slug: slugify(endpoint.summary),
      name: endpoint.summary || 'No Summary',
    description: cleanedDescription.trim(),
        embeddedSamples: embeddedSamples,
        method: method.toUpperCase(),
  path: path,
        parameters: allParameters || [],
        responses: responses,
     requestSample: requestSample,
  responseSample: responseSample,
    examples: {},
        security: endpoint.security || openApiSpec.security,
        baseUrl: endpoint['x-server-url'],
        isDownload: isDownload,
  downloadContentType: downloadContentType,
        downloadFileExtension: downloadFileExtension,
      };

      api.requestBody = requestBody;

      // Storing a template for initial display. Dynamic snippets will be generated in RightPanel.
      api.examples = {
   curl: generateCurlSnippet(api, {}, securitySchemes, null, true),
        javascript: generateJavascriptSnippet(api, {}, securitySchemes, null, true),
        python: generatePythonSnippet(api, {}, securitySchemes, null, true),
        ruby: generateRubySnippet(api, {}, securitySchemes, null, true),
 c: generateCSnippet(api, {}, securitySchemes, null, true),
        cpp: generateCppSnippet(api, {}, securitySchemes, null, true),
        java: generateJavaSnippet(api, {}, securitySchemes, null, true),
        shell: generateShellSnippet(api, {}, securitySchemes, null, true),
        node: generateNodeSnippet(api, {}, securitySchemes, null, true),
        php: generatePHPSnippet(api, {}, securitySchemes, null, true),
     csharp: generateCSharpSnippet(api, {}, securitySchemes, null, true),
    swift: generateSwiftSnippet(api, {}, securitySchemes, null, true),
  http: generateHTTPSnippet(api, {}, securitySchemes, null, true),
    };

      const tagName = endpoint.tags && endpoint.tags.length > 0 ? endpoint.tags[0] : 'default';
   if (!groupedApis[tagName]) {
        groupedApis[tagName] = [];
   }
  groupedApis[tagName].push(api);
  }
  }

  const finalApis = {};

  for (const path in openApiSpec.paths) {
    for (const method in openApiSpec.paths[path]) {
      const endpoint = openApiSpec.paths[path][method];
      const mainGroupName = endpoint['x-main-group'] || 'Partner Endpoints';
      const subGroupName = endpoint.tags && endpoint.tags.length > 0 ? endpoint.tags[0] : 'default';

  if (!finalApis[mainGroupName]) {
     finalApis[mainGroupName] = {
          slug: slugify(mainGroupName),
       groups: {}
        };
      }
      if (!finalApis[mainGroupName].groups[subGroupName]) {
  finalApis[mainGroupName].groups[subGroupName] = {
     name: subGroupName,
        slug: slugify(subGroupName),
 apis: []
        };
      }
      const api = groupedApis[subGroupName] && groupedApis[subGroupName].find(a => a.path === path && a.method === method.toUpperCase());
      if (api) {
   finalApis[mainGroupName].groups[subGroupName].apis.push(api);
      }
    }
  }

  // Apply configured endpoint ordering per tag.
  // Tags listed in TAG_ENDPOINT_ORDER will have their apis sorted accordingly;
  // all other tags keep the backend-provided order unchanged.
  for (const mainGroupName in finalApis) {
    for (const subGroupName in finalApis[mainGroupName].groups) {
      const subGroup = finalApis[mainGroupName].groups[subGroupName];
      subGroup.apis = sortApisByTagOrder(subGroup.apis, subGroupName);
    }
  }

  return { apis: finalApis, baseUrl: baseUrl, securitySchemes: securitySchemes };
};
